import { useRef } from 'react'
import { Box, Button, Paper, Text, Title } from '@mantine/core'
import { useTranslation } from 'react-i18next'

export const DropHighlight = () => {
  const { t } = useTranslation('documentation')
  return (
    <Box
      pos="absolute"
      inset={0}
      style={{
        zIndex: 300,
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'light-dark(rgba(231, 245, 255, 0.85), rgba(34, 139, 230, 0.22))',
        boxShadow: 'inset 0 0 0 4px var(--mantine-color-blue-6)',
      }}
    >
      <Title order={3} c="blue">
        {t('dropActive')}
      </Title>
    </Box>
  )
}

const DropScreen = ({
  hot,
  summary,
  status,
  statusError,
  onFiles,
  onCancel,
}: {
  hot: boolean
  summary: string | null
  status: string
  statusError: boolean
  onFiles: (files: File[]) => void
  onCancel?: () => void
}) => {
  const { t } = useTranslation('documentation')
  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <Box
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--mantine-spacing-xl)',
        background: hot ? 'light-dark(var(--mantine-color-blue-0), rgba(34, 139, 230, 0.22))' : undefined,
        boxShadow: hot ? 'inset 0 0 0 4px var(--mantine-color-blue-6)' : undefined,
        transition: 'background 120ms ease, box-shadow 120ms ease',
      }}
    >
      <Paper
        radius="md"
        maw={560}
        w="100%"
        p="xl"
        ta="center"
        style={{
          border: hot
            ? '3px solid var(--mantine-color-blue-6)'
            : '2px dashed light-dark(var(--mantine-color-gray-5), var(--mantine-color-dark-2))',
          background: hot ? 'light-dark(var(--mantine-color-blue-1), var(--mantine-color-dark-5))' : undefined,
          boxShadow: hot ? '0 0 0 8px light-dark(var(--mantine-color-blue-3), rgba(34, 139, 230, 0.45))' : undefined,
          transform: hot ? 'scale(1.03)' : undefined,
          transition: 'background 120ms ease, border-color 120ms ease, box-shadow 120ms ease, transform 120ms ease',
        }}
      >
        <Title order={3} mb="xs" c={hot ? 'blue' : undefined}>
          {hot ? t('dropActive') : t('dropTitle')}
        </Title>
        <Text c="dimmed" size="md" mb="lg">
          {t('dropBody')}
        </Text>
        <Button onClick={() => inputRef.current?.click()}>{t('chooseFile')}</Button>
        <input
          ref={inputRef}
          type="file"
          accept=".7z,.zip"
          hidden
          onChange={event => {
            onFiles([...(event.currentTarget.files ?? [])])
            event.currentTarget.value = ''
          }}
        />
        {summary ? (
          <Text size="sm" mt="md">
            {summary}
          </Text>
        ) : null}
        <Text size="sm" mt={6} c={statusError ? 'red' : 'dimmed'} mih={18}>
          {status}
        </Text>
        {onCancel ? (
          <Button mt="md" variant="default" onClick={onCancel}>
            {t('cancel')}
          </Button>
        ) : null}
      </Paper>
    </Box>
  )
}

export default DropScreen
